/**
 * Corpo HTML dei messaggi scritti nel gestionale (decisione di Davide del 2/11/2026: il compositore con
 * grassetto, corsivo, elenchi, collegamenti). Lo stesso codice gira nel browser, che scrive e mostra, e
 * nell'API, che ripulisce di nuovo prima di spedire: l'HTML che arriva dal client non e' mai fidato.
 *
 * Elenco chiuso di elementi, nessun attributo tranne href dei collegamenti (http, https, mailto): niente
 * stili, classi, immagini, tabelle, script. E' cosi' anche l'incolla da Word o da Outlook: resta la
 * struttura del testo, sparisce tutto il resto. Senza dipendenze e senza DOM, perche' l'API non ne ha.
 */

/** Elementi che restano, senza attributi (a con il solo href). */
const AMMESSI = new Set(['p', 'br', 'div', 'b', 'strong', 'i', 'em', 'u', 'ul', 'ol', 'li', 'blockquote', 'a'])

/** Elementi che spariscono con il loro contenuto. */
const CON_CONTENUTO = /<(script|style|head|title|iframe|object|embed|noscript|template|svg|math|xml|select|textarea|button|video|audio)\b[^>]*>[\s\S]*?<\/\1\s*>/gi

/** Il massimo che si accetta: oltre, il corpo si taglia (il testo semplice resta intero). */
export const HTML_POSTA_MASSIMO = 500_000

const escapa = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')

/** Le entita' piu' comuni, quanto basta per gli attributi e per il testo semplice. */
export function decodificaEntita(s: string): string {
  return s
    .replace(/&nbsp;/gi, ' ')
    .replace(/&#(\d+);/g, (_, n: string) => String.fromCodePoint(Math.min(Number(n), 0x10ffff)))
    .replace(/&#x([0-9a-f]+);/gi, (_, n: string) => String.fromCodePoint(Math.min(parseInt(n, 16), 0x10ffff)))
    .replace(/&lt;/gi, '<').replace(/&gt;/gi, '>').replace(/&quot;/gi, '"').replace(/&#39;|&apos;/gi, '\'')
    .replace(/&amp;/gi, '&')
}

/** L'indirizzo di un collegamento, se e' http, https o mailto; altrimenti null. */
export function hrefAmmesso(grezzo: string): string | null {
  const href = decodificaEntita(grezzo).trim()
  if (/^https?:\/\/[^\s<>"]+$/i.test(href)) return href
  if (/^mailto:[^\s<>"]+$/i.test(href)) return href
  return null
}

/** L'href di un tag <a ...>, fra virgolette doppie, semplici o senza. */
function hrefDi(attributi: string): string | null {
  const m = /(?:^|\s)href\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s"'>]+))/i.exec(attributi)
  return m ? hrefAmmesso(m[1] ?? m[2] ?? m[3] ?? '') : null
}

/**
 * L'HTML del corpo ripulito: solo gli elementi di AMMESSI, senza attributi, collegamenti http, https e
 * mailto. Il testo fra i tag resta com'e', con < e > sciolti in entita'.
 */
export function ripulisciHtmlPosta(html: string): string {
  let s = html.slice(0, HTML_POSTA_MASSIMO * 2)
    .replace(CON_CONTENUTO, '')
    .replace(/<!--[\s\S]*?-->/g, '')
    .replace(/<!\[[\s\S]*?\]>/g, '')
    .replace(/<![^>]*>/g, '')
    .replace(/<\?[\s\S]*?\?>/g, '')
  const fuori: string[] = []
  const aperti: string[] = []
  const tag = /<\s*(\/?)\s*([a-zA-Z][a-zA-Z0-9:-]*)([^>]*)>/g
  let ultimo = 0
  for (let m = tag.exec(s); m; m = tag.exec(s)) {
    fuori.push(s.slice(ultimo, m.index).replace(/</g, '&lt;').replace(/>/g, '&gt;'))
    ultimo = tag.lastIndex
    const chiude = m[1] === '/'
    const nome = m[2]!.toLowerCase()
    if (!AMMESSI.has(nome)) continue
    if (nome === 'br') { fuori.push('<br>'); continue }
    if (chiude) {
      // Si chiude solo un elemento aperto: un </a> o </ul> spaiato non esce.
      const posizione = aperti.lastIndexOf(nome)
      if (posizione < 0) continue
      for (const n of aperti.splice(posizione).reverse()) fuori.push(`</${n}>`)
      continue
    }
    if (nome === 'a') {
      const href = hrefDi(m[3] ?? '')
      if (!href) continue
      fuori.push(`<a href="${escapa(href)}" rel="noopener noreferrer">`)
    } else {
      fuori.push(`<${nome}>`)
    }
    aperti.push(nome)
  }
  fuori.push(s.slice(ultimo).replace(/</g, '&lt;').replace(/>/g, '&gt;'))
  for (const n of aperti.reverse()) fuori.push(`</${n}>`)
  s = fuori.join('')
  return s.length > HTML_POSTA_MASSIMO ? s.slice(0, HTML_POSTA_MASSIMO) : s.trim()
}

/**
 * Il testo semplice di un corpo ripulito, per la parte text/plain del messaggio e per la firma: righe
 * per paragrafi e a capo, «- » per le voci degli elenchi, l'indirizzo fra < > dopo il testo di un
 * collegamento che non lo mostra.
 */
export function testoDaHtml(html: string): string {
  return decodificaEntita(html
    .replace(/<a href="([^"]*)"[^>]*>([\s\S]*?)<\/a>/gi, (_, href: string, testo: string) => {
      const indirizzo = decodificaEntita(href).replace(/^mailto:/i, '')
      const visibile = testo.replace(/<[^>]*>/g, '')
      // Le parentesi in entita': i tag si tolgono dopo, l'indirizzo deve restare.
      return decodificaEntita(visibile).includes(indirizzo) ? testo : `${testo} &lt;${escapa(indirizzo)}&gt;`
    })
    .replace(/<br>/gi, '\n')
    .replace(/<li>/gi, '- ')
    .replace(/<\/(p|div|li|blockquote|ul|ol)>/gi, '\n')
    .replace(/<blockquote>/gi, '')
    .replace(/<[^>]*>/g, ''))
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
}

/** Il testo semplice come HTML: entita' sciolte, a capo come <br>. Per il primo disegno dell'editor. */
export function testoInHtml(testo: string): string {
  return escapa(testo).replace(/\r?\n/g, '<br>')
}
